import React, { useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import Input from "../../components/form/input/InputField";
import { Search } from "lucide-react";
import { isValidURL } from "@/utils/validation";
import FlashcardService from "@/services/flashcard.service";
import AdvertisementService from "@/services/advertisement.service";
import AdvertisementTable from "@/components/tables/AdvertisementTable";
import { useTranslation } from "react-i18next";

export default function ListAdvertisement() {
    const [advertisements, setAdvertisements] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const { t } = useTranslation();

    const fetchAdvertisements = async (page: number, search?: string) => {
        try {
            setLoading(true);
            setError("");
            const response = await AdvertisementService.getAdvertisement({
                page: page,
                limit: 10,
                search: search
            });
            let updatedAdvertisements = response.data.advertisements || [];
            for (const it of updatedAdvertisements) {
                if (it.image) {
                    const image = isValidURL(it.image) ? it.image : await fetchSignedUrl(it.image);
                    it.image = image;
                } else {
                    it.image = "";
                }
            }
            setAdvertisements(updatedAdvertisements);
            setTotalPages(Math.ceil((response.data.count || 0) / 10));
            setCurrentPage(page);
        } catch (err) {
            setError("Failed to fetch advertisements. Please try again later.");
            console.error("Error fetching advertisements:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchAdvertisements(1, searchQuery);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, []);

    const handlePageChange = (page: number) => {
        fetchAdvertisements(page, searchQuery);
    };

    return (
        <>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description="User Management"
            />
            <div className="space-y-6">
                <ComponentCard title="">
                    <div className="mb-4 relative">
                        <Input
                            type="text"
                            placeholder={t('Search advertisements')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onEnter={(value) => fetchAdvertisements(1, value)}
                            className="pl-10"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                    {loading ? (
                        <div className="animate-pulse">
                            <div className="space-y-4">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="flex items-center space-x-4 py-3">
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center p-4">{error}</div>
                    ) : (
                        <AdvertisementTable
                            advertisements={advertisements}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isLoading={loading}
                        />
                    )}
                </ComponentCard>
            </div>
        </>
    );
}
